import { useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Button, InputNumber, Select, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { IConditionalPrice, IConditionGroup } from '../../../types/configOption';
import type { IConfigOption } from '../../../types/configOption';
import type { ICategory } from '../../../types/categorySetup';

export interface ConditionalPricesFormRef {
  getPrices: () => IConditionalPrice[];
}

interface ConditionalPricesFormProps {
  initialPrices?: IConditionalPrice[];
  allOptions: IConfigOption[];
  categories: ICategory[];
}

// Internal row uses flat option IDs for the multi-select UI
interface RowState {
  flatOptionIds: string[];
  price: number;
}

// Flatten conditions[] back to a simple array of option IDs for the Select.
// Also handles legacy records saved with the old {optionIds[], price} shape.
const toFlatRows = (prices: IConditionalPrice[]): RowState[] =>
  prices.map(p => ({
    flatOptionIds: p.conditions
      ? p.conditions.flatMap(c => c.optionIds)
      : ((p as any).optionIds ?? []),
    price: p.price,
  }));

export const ConditionalPricesForm = forwardRef<ConditionalPricesFormRef, ConditionalPricesFormProps>(
  ({ initialPrices = [], allOptions, categories }, ref) => {
    const [rows, setRows] = useState<RowState[]>(
      initialPrices.length > 0 ? toFlatRows(initialPrices) : []
    );

    // Build option ID → categoryId lookup
    const optionCategoryMap = useMemo(() => {
      const map: Record<string, string> = {};
      allOptions.forEach(opt => {
        map[opt.dogma_configurationoptionsid] = opt._dogma_category_value || '';
      });
      return map;
    }, [allOptions]);

    // Build categoryId → categoryName lookup
    const categoryNameMap = useMemo(() => {
      const map: Record<string, string> = {};
      categories.forEach(cat => {
        map[cat.dogma_categoryid] = cat.dogma_name;
      });
      return map;
    }, [categories]);

    // On save: group flat option IDs by category → conditions[]
    useImperativeHandle(ref, () => ({
      getPrices: () =>
        rows.map(row => {
          const grouped = new Map<string, string[]>();
          row.flatOptionIds.forEach(optId => {
            const catId = optionCategoryMap[optId] || '';
            if (!grouped.has(catId)) grouped.set(catId, []);
            grouped.get(catId)!.push(optId);
          });
          const conditions: IConditionGroup[] = Array.from(grouped.entries()).map(
            ([categoryId, optionIds]) => ({ categoryId, optionIds })
          );
          return { conditions, price: row.price };
        }),
    }));

    // Flat list for the Select options
    const flatOptions = useMemo(() =>
      allOptions.map(opt => ({
        value: opt.dogma_configurationoptionsid,
        label: opt.dogma_name,
        categoryId: opt._dogma_category_value || '',
        categoryName: categoryNameMap[opt._dogma_category_value || ''] || 'Other',
        optionTypeName: opt['dogma_optiontype@OData.Community.Display.V1.FormattedValue'] || '',
        searchLabel: `${categoryNameMap[opt._dogma_category_value || ''] || ''} ${opt['dogma_optiontype@OData.Community.Display.V1.FormattedValue'] || ''} ${opt.dogma_name}`.toLowerCase(),
      })),
      [allOptions, categoryNameMap]
    );

    const addRow = () => {
      setRows(prev => [...prev, { flatOptionIds: [], price: 0 }]);
    };

    const removeRow = (index: number) => {
      setRows(prev => prev.filter((_, i) => i !== index));
    };

    const updateOptionIds = (index: number, flatOptionIds: string[]) => {
      setRows(prev => prev.map((row, i) => i === index ? { ...row, flatOptionIds } : row));
    };

    const updatePrice = (index: number, price: number | null) => {
      setRows(prev => prev.map((row, i) => i === index ? { ...row, price: price ?? 0 } : row));
    };

    // Derive grouping summary for a row (to show AND/OR logic)
    const getGroupSummary = (flatOptionIds: string[]) => {
      const grouped = new Map<string, string[]>();
      flatOptionIds.forEach(optId => {
        const catId = optionCategoryMap[optId] || '';
        if (!grouped.has(catId)) grouped.set(catId, []);
        grouped.get(catId)!.push(optId);
      });
      return Array.from(grouped.entries()).map(([catId, ids]) => ({
        categoryName: categoryNameMap[catId] || 'Other',
        count: ids.length,
      }));
    };

    return (
      <div>
        <p className="text-xs text-gray-400 mb-4">
          Select options from one or more categories. Options within the same category are <strong>OR</strong> combined; options across different categories are <strong>AND</strong> combined.
        </p>

        {rows.length > 0 && (
          <div className="grid grid-cols-[1fr_140px_32px] gap-x-3 mb-2 px-1">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              When these options are selected
            </span>
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
              Price (£)
            </span>
            <span />
          </div>
        )}

        {rows.map((row, index) => {
          const groupSummary = getGroupSummary(row.flatOptionIds);
          return (
            <div key={index} className="mb-4">
              <div className="grid grid-cols-[1fr_140px_32px] gap-x-3 items-start">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Search and select options..."
                  value={row.flatOptionIds}
                  onChange={(val) => updateOptionIds(index, val)}
                  filterOption={(input, option) => {
                    const match = flatOptions.find(o => o.value === option?.value);
                    return match ? match.searchLabel.includes(input.toLowerCase()) : false;
                  }}
                  optionRender={(option) => {
                    const match = flatOptions.find(o => o.value === option.value);
                    return (
                      <div className="flex items-center gap-1">
                        <Tag color="blue" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                          {match?.categoryName}
                        </Tag>
                        {match?.optionTypeName && (
                          <Tag color="purple" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                            {match.optionTypeName}
                          </Tag>
                        )}
                        <span className="text-sm">{option.label}</span>
                      </div>
                    );
                  }}
                  options={flatOptions.map(o => ({ value: o.value, label: o.label }))}
                />

                <InputNumber
                  prefix="£"
                  precision={2}
                  min={0}
                  value={row.price}
                  onChange={(val) => updatePrice(index, val)}
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />

                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRow(index)}
                />
              </div>

              {groupSummary.length > 1 && (
                <div className="mt-1 flex flex-wrap gap-1 items-center">
                  {groupSummary.map((g, gi) => (
                    <span key={gi} className="flex items-center gap-1">
                      {gi > 0 && (
                        <span className="text-[10px] font-bold text-orange-500 px-1">AND</span>
                      )}
                      <Tag color="geekblue" style={{ fontSize: 10, margin: 0 }}>
                        {g.categoryName}: {g.count} option{g.count > 1 ? 's' : ''}
                      </Tag>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-6 text-center text-xs text-gray-400 mb-4">
            No conditional prices defined.
            <br />
            This option uses its default price for all selections.
          </div>
        )}

        <Button
          type="dashed"
          onClick={addRow}
          icon={<PlusOutlined />}
          size="small"
          block
        >
          Add Price Rule
        </Button>
      </div>
    );
  }
);

ConditionalPricesForm.displayName = 'ConditionalPricesForm';
